import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Loader2, Send, Save, Plus, Trash2, PoundSterling, Edit, AlertCircle, Star } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { MATERIALS, LOUVRE_SIZES, WINDOW_TYPES } from './constants';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AmendSurveyDialog = ({ survey, open, onOpenChange, onAmended }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingQuote, setSendingQuote] = useState(false);
  const [step, setStep] = useState(1);
  const [customTotalPrice, setCustomTotalPrice] = useState(null);
  const [materialOverrides, setMaterialOverrides] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    customer_postcode: '',
    property_type: 'house',
    surveyor_name: '',
    general_notes: '',
    access_notes: '',
    installation_notes: '',
    customer_notes: '',
    openings: [],
    include_blinds: false,
    blinds: [],
    vat_rate: 20,
    selected_quote_materials: ['paulownia'],
  });

  // Load survey data when opened
  useEffect(() => {
    if (survey && open) {
      setFormData({
        customer_name: survey.customer_name || '',
        customer_email: survey.customer_email || '',
        customer_phone: survey.customer_phone || '',
        customer_address: survey.customer_address || '',
        customer_postcode: survey.customer_postcode || '',
        property_type: survey.property_type || 'house',
        surveyor_name: survey.surveyor_name || '',
        general_notes: survey.general_notes || '',
        access_notes: survey.access_notes || '',
        installation_notes: survey.installation_notes || '',
        customer_notes: '',
        // Survey-level defaults
        material: survey.material || 'paulownia',
        louvre_size: survey.louvre_size || '76mm',
        colour: survey.colour || 'White',
        control_type: survey.control_type || 'hidden_tilt_rod',
        stiles: survey.stiles || 'beaded',
        hinge_colour: survey.hinge_colour || 'White',
        openings: (survey.openings || []).map((o, idx) => ({
          ...o,
          opening_id: o.opening_id || `opening_${idx}`,
          material: o.material || survey.material || 'paulownia',
          louvre_size: o.louvre_size || survey.louvre_size || '76mm',
          colour: o.colour || survey.colour || 'White',
          control_type: o.control_type || survey.control_type || 'hidden_tilt_rod',
          stiles: o.stiles || survey.stiles || 'beaded'
        })),
        include_blinds: (survey.blinds && survey.blinds.length > 0) || false,
        blinds: survey.blinds || [],
        vat_rate: survey.vat_rate ?? 20,
        selected_quote_materials: survey.selected_quote_materials || ['paulownia'],
      });
      setCustomTotalPrice(null);
      setMaterialOverrides({});
      setStep(1);
    }
  }, [survey, open]);

  // Calculate pricing for ALL materials — same engine as CreateSurveyDialog
  const pricingData = useMemo(() => {
    let totalSqm = 0;
    const vatRate = formData.vat_rate ?? 20;
    const vatMultiplier = vatRate === 0 ? (1 / 1.20) : 1;
    const selectedMats = formData.selected_quote_materials || ['paulownia'];

    const openingSqms = formData.openings.map(opening => {
      const width = parseFloat(opening.width) || 0;
      const height = parseFloat(opening.height) || 0;
      const sqm = (width * height) / 1000000;
      totalSqm += sqm;
      return { ...opening, sqm, label: opening.room_location || 'Opening', layout_code: opening.layout || 'N/A' };
    });

    const allMaterials = {};
    MATERIALS.forEach(mat => {
      const baseRetail = openingSqms.reduce((sum, o) => sum + Math.round(o.sqm * mat.pricePerSqm * 100) / 100, 0);
      const retailWithVat = Math.round(baseRetail * vatMultiplier * 100) / 100;
      const overrideVal = materialOverrides[mat.id];
      const hasOverride = overrideVal !== undefined && overrideVal !== null && overrideVal !== '';
      const retailTotal = hasOverride ? parseFloat(overrideVal) : retailWithVat;
      const rrp = retailWithVat;

      let costTotal = 0;
      if (mat.costPerSqm !== null) {
        costTotal = Math.round(openingSqms.reduce((sum, o) => sum + o.sqm * mat.costPerSqm, 0) * 100) / 100;
      }

      const upfrontRatio = mat.upfrontPricePerSqm / mat.pricePerSqm;
      const upfrontTotal = Math.round(retailTotal * upfrontRatio * 100) / 100;
      const margin = mat.costPerSqm !== null ? Math.round((retailTotal - costTotal) * 100) / 100 : null;
      const marginPct = mat.costPerSqm !== null && retailTotal > 0 ? Math.round((margin / retailTotal) * 100) : null;
      const effectiveSqm = hasOverride && totalSqm > 0 ? Math.round(retailTotal / totalSqm * 100) / 100 : null;
      const discount = Math.round((rrp - retailTotal) * 100) / 100;

      const ratio = retailWithVat > 0 ? retailTotal / retailWithVat : 1;
      const windowPrices = openingSqms.map(o => ({
        label: o.label,
        sqm: o.sqm,
        standard: Math.round(o.sqm * mat.pricePerSqm * vatMultiplier * ratio * 100) / 100,
        upfront: Math.round(o.sqm * mat.upfrontPricePerSqm * vatMultiplier * ratio * 100) / 100,
      }));

      allMaterials[mat.id] = {
        id: mat.id, name: mat.name, leadTime: mat.leadTime,
        retailTotal: Math.round(retailTotal * 100) / 100,
        rrp: Math.round(rrp * 100) / 100,
        discount: discount > 0.01 ? discount : 0,
        hasDiscount: discount > 0.01,
        upfrontTotal: Math.round(upfrontTotal * 100) / 100,
        deposit: Math.round(retailTotal * 0.6 * 100) / 100,
        balance: Math.round(retailTotal * 0.4 * 100) / 100,
        costTotal, margin, marginPct,
        hasCost: mat.costPerSqm !== null,
        pricePerSqm: mat.pricePerSqm, costPerSqm: mat.costPerSqm,
        hasOverride, effectiveSqm, windowPrices,
        isSelected: selectedMats.includes(mat.id),
      };
    });

    return {
      totalSqm: totalSqm.toFixed(2),
      vatRate,
      materials: allMaterials,
      selectedMaterials: selectedMats,
      openingSqms,
    };
  }, [formData.openings, formData.vat_rate, formData.selected_quote_materials, materialOverrides]);

  const updateOpening = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      openings: prev.openings.map((o, i) => i === index ? { ...o, [field]: value } : o)
    }));
  };

  const addOpening = () => {
    const newOpening = {
      opening_id: `new_opening_${Date.now()}`,
      room_location: `Window ${formData.openings.length + 1}`,
      window_type: 'flat_window',
      layout: '',
      width: 1000,
      height: 1200,
      material: 'paulownia',
      louvre_size: '89mm',
      colour: 'White',
      notes: ''
    };
    setFormData(prev => ({
      ...prev,
      openings: [...prev.openings, newOpening]
    }));
  };

  const removeOpening = (index) => {
    if (formData.openings.length <= 1) {
      toast({ title: "Cannot Remove", description: "Survey must have at least one opening", variant: "destructive" });
      return;
    }
    setFormData(prev => ({ ...prev, openings: prev.openings.filter((_, i) => i !== index) }));
  };

  // Blinds helpers
  const addBlind = () => setFormData(prev => ({ ...prev, blinds: [...prev.blinds, { room_name: '', description: '', price: '' }] }));
  const updateBlind = (index, field, value) => setFormData(prev => ({ ...prev, blinds: prev.blinds.map((b, i) => i === index ? { ...b, [field]: value } : b) }));
  const removeBlind = (index) => setFormData(prev => ({ ...prev, blinds: prev.blinds.filter((_, i) => i !== index) }));
  const blindsTotal = useMemo(() => formData.blinds.reduce((sum, b) => sum + (parseFloat(b.price) || 0), 0), [formData.blinds]);

  // Save survey amendments only (no quote)
  const handleSaveSurvey = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      const updatePayload = {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        customer_address: formData.customer_address,
        customer_postcode: formData.customer_postcode,
        property_type: formData.property_type,
        surveyor_name: formData.surveyor_name,
        general_notes: formData.general_notes,
        access_notes: formData.access_notes,
        installation_notes: formData.installation_notes,
        openings: formData.openings.map(o => ({
          room_location: o.room_location,
          window_type: o.window_type || 'flat_window',
          layout: o.layout,
          width: parseFloat(o.width) || 0,
          height: parseFloat(o.height) || 0,
          material: o.material,
          louvre_size: o.louvre_size,
          colour: o.colour,
          notes: o.notes
        }))
      };

      const response = await axios.put(
        `${API}/api/surveys/${survey.id}`,
        updatePayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast({
          title: "Survey Updated",
          description: `Survey ${survey.survey_number} has been amended`
        });
        onOpenChange(false);
        onAmended?.();
      } else {
        throw new Error(response.data.data?.error || 'Failed to update survey');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to update survey",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // Save survey and send new/amended quote with fresh nurturing
  const handleSaveAndSendQuote = async () => {
    if (!formData.customer_email) {
      toast({
        title: "Email Required",
        description: "Customer email is required to send the quote",
        variant: "destructive"
      });
      return;
    }

    setSendingQuote(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      // First update the survey
      const updatePayload = {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        customer_address: formData.customer_address,
        customer_postcode: formData.customer_postcode,
        property_type: formData.property_type,
        surveyor_name: formData.surveyor_name,
        general_notes: formData.general_notes,
        access_notes: formData.access_notes,
        installation_notes: formData.installation_notes,
        openings: formData.openings.map(o => ({
          room_location: o.room_location,
          window_type: o.window_type || 'flat_window',
          layout: o.layout,
          width: parseFloat(o.width) || 0,
          height: parseFloat(o.height) || 0,
          material: o.material,
          louvre_size: o.louvre_size,
          colour: o.colour,
          notes: o.notes
        }))
      };

      await axios.put(
        `${API}/api/surveys/${survey.id}`,
        updatePayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Build quote payload — only selected materials, NO cost prices
      const selectedMats = formData.selected_quote_materials || ['paulownia'];
      const primaryMat = pricingData.materials[selectedMats[0]] || pricingData.materials['paulownia'];
      
      const windowsPayload = pricingData.openingSqms.map((o, i) => {
        const w = { label: o.label || 'Window', layout_code: o.layout_code || 'N/A', width: parseFloat(o.width) || 0, height: parseFloat(o.height) || 0, sqm: o.sqm, price: primaryMat.windowPrices[i]?.standard || 0 };
        selectedMats.forEach(matId => {
          const mp = pricingData.materials[matId];
          if (mp) {
            w[`${matId}_standard`] = mp.windowPrices[i]?.standard || 0;
            w[`${matId}_upfront`] = mp.windowPrices[i]?.upfront || 0;
          }
        });
        return w;
      });

      const pricingOptions = {};
      selectedMats.forEach(matId => {
        const mp = pricingData.materials[matId];
        if (mp) {
          pricingOptions[matId] = {
            standard_total: mp.retailTotal,
            rrp_total: mp.rrp,
            upfront_total: mp.upfrontTotal,
            deposit: mp.deposit,
            balance: mp.balance,
            price_per_sqm: mp.pricePerSqm,
          };
        }
      });

      const quotePayload = {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone || '',
        customer_address: formData.customer_address || '',
        material: primaryMat.name,
        louvre_size: formData.louvre_size || '76mm',
        colour: formData.colour || 'White',
        control_type: formData.control_type || 'hidden_tilt_rod',
        stiles: formData.stiles || 'beaded',
        hinge_colour: formData.hinge_colour || 'White',
        vat_rate: formData.vat_rate ?? 20,
        windows: windowsPayload,
        total_price: primaryMat.retailTotal + blindsTotal,
        customer_notes: formData.customer_notes || null,
        blinds: formData.include_blinds && formData.blinds.length > 0 
          ? formData.blinds.filter(b => b.room_name || b.description).map(b => ({
              room_name: b.room_name || 'Blind',
              description: b.description || '',
              price: parseFloat(b.price) || 0
            }))
          : [],
        pricing_options: pricingOptions,
        is_amendment: true,
        original_survey_number: survey.survey_number
      };

      const quoteResponse = await axios.post(`${API}/api/quotes/send-formal-quote`, quotePayload);

      if (quoteResponse.data.success) {
        toast({
          title: "Survey Updated & Quote Sent!",
          description: `Amended quote sent to ${formData.customer_email}. New nurture campaign started.`
        });
        onOpenChange(false);
        onAmended?.();
      } else {
        throw new Error(quoteResponse.data.detail || 'Failed to send quote');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to save and send quote",
        variant: "destructive"
      });
    } finally {
      setSendingQuote(false);
    }
  };

  if (!survey) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5 text-[#8CB89F]" />
            Amend Survey {survey.survey_number}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={`step-${step}`} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="step-1" onClick={() => setStep(1)}>1. Customer Details</TabsTrigger>
            <TabsTrigger value="step-2" onClick={() => setStep(2)}>2. Openings</TabsTrigger>
            <TabsTrigger value="step-3" onClick={() => setStep(3)}>3. Review & Send</TabsTrigger>
          </TabsList>

          {/* Step 1: Customer Details */}
          <TabsContent value="step-1" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer_name">Customer Name *</Label>
                <Input
                  id="customer_name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_email">Email</Label>
                <Input
                  id="customer_email"
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_phone">Phone</Label>
                <Input
                  id="customer_phone"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_postcode">Postcode</Label>
                <Input
                  id="customer_postcode"
                  value={formData.customer_postcode}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_postcode: e.target.value }))}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="customer_address">Address</Label>
                <Input
                  id="customer_address"
                  value={formData.customer_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_address: e.target.value }))}
                  placeholder="Full address"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="surveyor_name">Surveyor Name</Label>
                <Input
                  id="surveyor_name"
                  value={formData.surveyor_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, surveyor_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="property_type">Property Type</Label>
                <Select 
                  value={formData.property_type} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, property_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="house">House</SelectItem>
                    <SelectItem value="flat">Flat</SelectItem>
                    <SelectItem value="bungalow">Bungalow</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={() => setStep(2)} disabled={!formData.customer_name}>
                Next: Edit Openings
              </Button>
            </div>
          </TabsContent>

          {/* Step 2: Openings */}
          <TabsContent value="step-2" className="space-y-4 mt-4">
            {/* Live Pricing Banner */}
            {formData.openings.length > 0 && (
              <div className="bg-gradient-to-r from-[#2F4F4F] to-[#3d6363] text-white p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white/70">Live Quote Total</p>
                    <p className="text-2xl font-bold">£{pricingData.totalPrice.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-white/70">
                      {pricingData.materialsUsed.length === 1 
                        ? pricingData.materialsUsed[0]
                        : `${pricingData.materialsUsed.length} materials mixed`
                      }
                    </p>
                    <p className="text-sm text-white/70">{pricingData.totalSqm} m² total</p>
                  </div>
                </div>
              </div>
            )}

            {/* Add Opening Button */}
            <div className="flex justify-between items-center">
              <Label className="text-base font-medium">Openings ({formData.openings.length})</Label>
              <Button onClick={addOpening} size="sm" variant="outline" className="flex items-center gap-1">
                <Plus className="h-4 w-4" />
                Add Opening
              </Button>
            </div>

            {/* Openings List */}
            <div className="space-y-4 max-h-[400px] overflow-y-auto">
              {formData.openings.map((opening, index) => {
                const windowPrice = pricingData.windowPrices[index];
                return (
                  <div key={opening.opening_id || index} className="bg-gray-50 p-4 rounded-lg border space-y-3">
                    <div className="flex justify-between items-center">
                      <Input
                        value={opening.room_location}
                        onChange={(e) => updateOpening(index, 'room_location', e.target.value)}
                        className="w-48 font-medium"
                        placeholder="Room name"
                      />
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-[#2F4F4F]">
                          £{windowPrice?.price?.toLocaleString('en-GB', { minimumFractionDigits: 2 }) || '0.00'}
                        </span>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => removeOpening(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-5 gap-3">
                      <div>
                        <Label className="text-xs">Width (mm)</Label>
                        <Input
                          type="number"
                          value={opening.width || ''}
                          onChange={(e) => updateOpening(index, 'width', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Height (mm)</Label>
                        <Input
                          type="number"
                          value={opening.height || ''}
                          onChange={(e) => updateOpening(index, 'height', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Layout Code</Label>
                        <Input
                          value={opening.layout || ''}
                          onChange={(e) => updateOpening(index, 'layout', e.target.value)}
                          placeholder="e.g., 2-2"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Louvre Size</Label>
                        <Select 
                          value={opening.louvre_size || '89mm'} 
                          onValueChange={(v) => updateOpening(index, 'louvre_size', v)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LOUVRE_SIZES.map(size => (
                              <SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Material</Label>
                        <Select 
                          value={opening.material || 'paulownia'} 
                          onValueChange={(v) => updateOpening(index, 'material', v)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {MATERIALS.map(m => (
                              <SelectItem key={m.id} value={m.id}>
                                {m.name} - £{m.pricePerSqm}/m²
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="text-xs text-gray-500">
                      {((parseFloat(opening.width) || 0) * (parseFloat(opening.height) || 0) / 1000000).toFixed(2)} m²
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Blinds Section */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="amend_include_blinds"
                  checked={formData.include_blinds}
                  onChange={(e) => setFormData(prev => ({ ...prev, include_blinds: e.target.checked, blinds: e.target.checked && prev.blinds.length === 0 ? [{ room_name: '', description: '', price: '' }] : prev.blinds }))}
                  className="w-4 h-4 rounded"
                />
                <Label htmlFor="amend_include_blinds" className="cursor-pointer font-medium">Include Blinds</Label>
              </div>
              {formData.include_blinds && (
                <div className="space-y-2">
                  {formData.blinds.map((blind, idx) => (
                    <div key={idx} className="grid grid-cols-4 gap-2 items-end">
                      <div>
                        <Label className="text-xs">Room</Label>
                        <Input value={blind.room_name} onChange={(e) => updateBlind(idx, 'room_name', e.target.value)} placeholder="e.g. Bathroom" />
                      </div>
                      <div>
                        <Label className="text-xs">Description</Label>
                        <Input value={blind.description} onChange={(e) => updateBlind(idx, 'description', e.target.value)} placeholder="e.g. Roller Blind" />
                      </div>
                      <div>
                        <Label className="text-xs">Price (£)</Label>
                        <Input type="number" value={blind.price} onChange={(e) => updateBlind(idx, 'price', e.target.value)} placeholder="0.00" />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeBlind(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addBlind}><Plus className="h-3 w-3 mr-1" />Add Blind</Button>
                </div>
              )}
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={() => setStep(3)}>Next: Review & Send</Button>
            </div>
          </TabsContent>

          {/* Step 3: Review & Send */}
          <TabsContent value="step-3" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="customer_notes">Customer Notes (included in quote email)</Label>
                <Textarea
                  id="customer_notes"
                  value={formData.customer_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_notes: e.target.value }))}
                  placeholder="Add a personalized message for the customer..."
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="general_notes">Internal Notes (staff only)</Label>
                <Textarea
                  id="general_notes"
                  value={formData.general_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, general_notes: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>

            {/* Material Selection & Pricing — Surveyor View */}
            {formData.openings.length > 0 && (
              <div className="bg-gradient-to-br from-[#2F4F4F] to-[#3d6363] text-white p-5 rounded-xl">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <PoundSterling className="w-5 h-5" />
                    <h4 className="font-semibold text-lg">Pricing & Material Selection</h4>
                  </div>
                  <span className="text-xs text-white/50">{pricingData.totalSqm} m² total</span>
                </div>

                <p className="text-xs text-white/50 mb-3 uppercase tracking-wider">Tick materials to include in customer quote</p>

                <div className="space-y-2">
                  {MATERIALS.map(mat => {
                    const mp = pricingData.materials[mat.id];
                    if (!mp) return null;
                    const isSelected = (formData.selected_quote_materials || []).includes(mat.id);
                    
                    const toggleMaterial = () => {
                      setFormData(prev => {
                        const current = prev.selected_quote_materials || ['paulownia'];
                        if (current.includes(mat.id)) {
                          if (current.length <= 1) return prev;
                          return { ...prev, selected_quote_materials: current.filter(id => id !== mat.id) };
                        }
                        return { ...prev, selected_quote_materials: [...current, mat.id] };
                      });
                    };

                    return (
                      <div key={mat.id} className={`rounded-lg p-3 transition-all ${isSelected ? 'bg-white/15 border border-white/30' : 'bg-white/5 border border-transparent'}`}>
                        <div className="flex items-center gap-3">
                          <input type="checkbox" checked={isSelected} onChange={toggleMaterial} className="w-4 h-4 rounded" />
                          <div className="flex-1 min-w-0">
                            <span className="font-semibold text-sm">{mat.name}</span>
                            <span className="text-[10px] text-white/40 ml-2">{mat.leadTime}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-[10px] text-white/40 block">Retail</span>
                            <div className="flex items-center gap-1">
                              <span className="text-white/50 text-xs">£</span>
                              <Input type="number" step="0.01"
                                value={materialOverrides[mat.id] !== undefined ? materialOverrides[mat.id] : mp.rrp}
                                onChange={(e) => setMaterialOverrides(prev => ({ ...prev, [mat.id]: e.target.value === '' ? undefined : e.target.value }))}
                                className="w-24 bg-white/10 border-white/30 text-white text-sm font-bold text-right h-7 px-1.5" />
                            </div>
                          </div>
                          <div className="text-right w-20">
                            <span className="text-[10px] text-white/40 block">Cost</span>
                            {mp.hasCost ? <span className="text-sm text-red-300/80">£{mp.costTotal.toFixed(2)}</span> : <span className="text-xs text-white/30">—</span>}
                          </div>
                          <div className="text-right w-20">
                            <span className="text-[10px] text-white/40 block">Margin</span>
                            {mp.margin !== null ? <span className={`text-sm font-medium ${mp.margin > 0 ? 'text-green-300' : 'text-red-300'}`}>£{mp.margin.toFixed(2)}</span> : <span className="text-xs text-white/30">—</span>}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="mt-2 pt-2 border-t border-white/10 grid grid-cols-3 gap-2 text-xs text-white/60">
                            <div>Deposit: <span className="text-white/80 font-medium">£{mp.deposit.toFixed(2)}</span></div>
                            <div>Balance: <span className="text-white/80 font-medium">£{mp.balance.toFixed(2)}</span></div>
                            <div>Upfront (10% off): <span className="text-[#8CB89F] font-medium">£{mp.upfrontTotal.toFixed(2)}</span></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-red-300/60 mt-2">Cost prices are for internal use only — never shown to customers</p>

                {/* Overall Quote Total Override */}
                {(() => {
                  const selectedMats = formData.selected_quote_materials || ['paulownia'];
                  const primaryId = selectedMats[0];
                  const primaryMat = pricingData.materials[primaryId];
                  if (!primaryMat) return null;
                  const shuttersTotal = primaryMat.retailTotal;
                  const grandTotal = shuttersTotal + blindsTotal;
                  return (
                    <div className="border-t border-white/20 mt-4 pt-3">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-lg font-bold">Total Quote</span>
                          <span className="text-xs text-white/40 ml-2">({primaryMat.name}{blindsTotal > 0 ? ' + Blinds' : ''})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-white/70 text-lg">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={grandTotal.toFixed(2)}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value);
                              if (!isNaN(val) && val >= 0) {
                                const newShutters = val - blindsTotal;
                                if (newShutters >= 0) {
                                  setMaterialOverrides(prev => ({ ...prev, [primaryId]: newShutters.toFixed(2) }));
                                }
                              } else if (e.target.value === '') {
                                setMaterialOverrides(prev => { const n = {...prev}; delete n[primaryId]; return n; });
                              }
                            }}
                            className="w-32 bg-white/10 border-white/30 text-white text-xl font-bold text-right h-10 px-2"
                            data-testid="total-quote-override"
                          />
                        </div>
                      </div>
                      {blindsTotal > 0 && (
                        <div className="flex justify-between text-xs text-white/50 mt-1">
                          <span>Shutters: £{shuttersTotal.toFixed(2)} + Blinds: £{blindsTotal.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm text-white/60 mt-2">
                        <span>Deposit (60%)</span>
                        <span>£{(grandTotal * 0.6).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-white/60">
                        <span>Balance (40%)</span>
                        <span>£{(grandTotal * 0.4).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-[#8CB89F] mt-1">
                        <span>Pay Upfront (10% off)</span>
                        <span className="font-semibold">£{(grandTotal * 0.9).toFixed(2)}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Info about nurturing */}
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Sending a new quote will:</p>
                  <ul className="list-disc ml-4 mt-1 space-y-1">
                    <li>Create a fresh quote with a new reference number</li>
                    <li>Start a new email nurture sequence (Day 3, 7, 14, 29)</li>
                    <li>Cancel any existing nurture emails for previous quotes</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 pt-4">
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleSaveSurvey} 
                    disabled={saving || sendingQuote || !formData.customer_name}
                    variant="outline"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Survey Only
                  </Button>
                  <Button 
                    onClick={handleSaveAndSendQuote} 
                    disabled={saving || sendingQuote || !formData.customer_name || !formData.customer_email}
                    className="bg-[#8CB89F] hover:bg-[#7aa88f]"
                  >
                    {sendingQuote ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Save & Send New Quote
                  </Button>
                </div>
              </div>
              {!formData.customer_email && (
                <p className="text-sm text-amber-600 text-right">
                  Add customer email to enable "Send New Quote"
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AmendSurveyDialog;
